import React, { useState, useEffect, useMemo } from 'react';
import Loading from '../../Common/Loading'; // Adjust path if needed

// --- Interfaces ---
interface TutorRecord {
    tutor_name: string;
    tutor_login: string;
    as_of_today_families: number;
    as_of_today_high_priority: number;
    as_of_today_high_priority_due: number;
    oldest: string;
    future: number;
    total: number;
}

type SortKey = keyof Omit<TutorRecord, 'tutor_login'>;

const TutorSupportReport: React.FC = () => {
   const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

    // --- State ---
    const [data, setData] = useState<TutorRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' } | null>(null);

    // --- Fetch Data ---
    useEffect(() => {
        // Set body background to match screenshot
        document.body.style.backgroundColor = '#ffffff';

        const fetchReport = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_BASE_URL}api/tutor_support_report`);
                const json = await res.json();
                
                if (json.error) {
                    alert(`Database Error: ${json.error}`);
                } else {
                    setData(json.results || []);
                }
            } catch (err) {
                console.error("Fetch Error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchReport();
    }, []);

    // --- Sorting Logic ---
    const handleSort = (key: SortKey) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedData = useMemo(() => {
        let sortableItems = [...data];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [data, sortConfig]);

    // --- Totals Calculation (Footer Row) ---
    const totals = useMemo(() => {
        return data.reduce((acc, row) => {
            acc.as_of_today_families += row.as_of_today_families;
            acc.as_of_today_high_priority += row.as_of_today_high_priority;
            acc.as_of_today_high_priority_due += row.as_of_today_high_priority_due;
            acc.future += row.future;
            acc.total += row.total;
            return acc;
        }, {
            as_of_today_families: 0,
            as_of_today_high_priority: 0,
            as_of_today_high_priority_due: 0,
            future: 0,
            total: 0
        });
    }, [data]);

    // --- Handlers ---
    const handleBack = () => {
        window.location.href = "/Sysadmin";
    };

    // --- Styles matching the screenshot perfectly ---
    const styles = {
        body: { fontFamily: 'Arial, sans-serif', padding: '20px', minHeight: '100vh' },
        // inline-block forces the wrapper to be exactly as wide as the table, 
        // allowing the header text to center over it and the back button to align to its right edge.
        wrapper: { display: 'inline-block', minWidth: '800px' }, 
        
        headerContainer: { position: 'relative' as const, marginBottom: '5px', textAlign: 'center' as const, width: '100%' },
        title: { fontSize: '20pt', fontWeight: 'bold', color: 'black', margin: 0 },
        backBtn: { 
            position: 'absolute' as const, 
            right: 0, 
            bottom: '5px', 
            backgroundColor: '#328da8', 
            color: 'white', 
            fontWeight: 'bold', 
            fontSize: '9pt', 
            padding: '5px 15px', 
            border: '1px solid #000', 
            cursor: 'pointer', 
            borderRadius: '2px' 
        },
        
        table: { width: '100%', borderCollapse: 'collapse' as const, border: '1px solid #072129' },
        th: { backgroundColor: '#328da8', color: '#ffffff', padding: '8px 20px', textAlign: 'left' as const, border: '1px solid #072129', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
        td: { padding: '10px 7px', border: '1px solid #072129', color: 'black', fontSize: '15px', textAlign: 'left' as const },
        
        footerTh: { backgroundColor: '#e9f3f7', padding: '10px 7px', border: '1px solid #072129', color: 'black', textAlign: 'left' as const, fontSize: '13px', fontWeight: 'normal' },
        link: { color: '#337ab7', textDecoration: 'none' } // Classic link blue
    };

    return (
        <div style={styles.body}>
            {loading && <Loading />}

            <div style={styles.wrapper}>
                
                {/* Header Area */}
                <div style={styles.headerContainer}>
                    <div style={styles.title}>Tutor Support Report</div>
                    <button style={styles.backBtn} onClick={handleBack}>Back</button>
                </div>

                {/* Empty State */}
                {!loading && data.length === 0 && (
                    <div style={{ fontSize: '20pt', color: 'black', marginTop: '20px' }}>
                        No Extension Student
                    </div>
                )}

                {/* Grid Area */}
                {data.length > 0 && (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, width: '50px' }}>Sl No</th>
                                <th style={styles.th} onClick={() => handleSort('tutor_name')}>Tutor Name {sortConfig?.key === 'tutor_name' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}</th>
                                <th style={styles.th} onClick={() => handleSort('as_of_today_families')}>As Of Today Families {sortConfig?.key === 'as_of_today_families' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}</th>
                                <th style={styles.th} onClick={() => handleSort('as_of_today_high_priority')}>As Of Today High Priority {sortConfig?.key === 'as_of_today_high_priority' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}</th>
                                <th style={styles.th} onClick={() => handleSort('as_of_today_high_priority_due')}>As Of Today High Priority(Overdue) {sortConfig?.key === 'as_of_today_high_priority_due' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}</th>
                                <th style={styles.th} onClick={() => handleSort('oldest')}>Oldest {sortConfig?.key === 'oldest' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}</th>
                                <th style={styles.th} onClick={() => handleSort('future')}>Future {sortConfig?.key === 'future' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}</th>
                                <th style={styles.th} onClick={() => handleSort('total')}>Total {sortConfig?.key === 'total' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedData.map((row, index) => {
                                // Replicating the exact row coloration from the image
                                const bgColor = index % 2 === 0 ? '#e9f3f7' : '#ffffff';
                                
                                return (
                                    <tr key={row.tutor_login} style={{ backgroundColor: bgColor }}>
                                        <td style={styles.td}>{index + 1}</td>
                                        <td style={styles.td}>
                                            <a href={`/SYSADMIN/Reports/TutorSupportList?Tutor=${row.tutor_login}`} style={styles.link}>
                                                {row.tutor_name}
                                            </a>
                                        </td>
                                        <td style={styles.td}>{row.as_of_today_families}</td>
                                        <td style={styles.td}>{row.as_of_today_high_priority}</td>
                                        <td style={styles.td}>{row.as_of_today_high_priority_due}</td>
                                        <td style={styles.td}>{row.oldest}</td>
                                        <td style={styles.td}>{row.future}</td>
                                        <td style={styles.td}>{row.total}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            {/* Footer Row matching the Light Blue Color */}
                            <tr style={{ backgroundColor: '#e9f3f7' }}>
                                <td style={styles.footerTh}>{data.length + 1}</td>
                                <td style={styles.footerTh}>
                                    <span style={styles.link}>Total</span>
                                </td>
                                <td style={styles.footerTh}>{totals.as_of_today_families}</td>
                                <td style={styles.footerTh}>{totals.as_of_today_high_priority}</td>
                                <td style={styles.footerTh}>{totals.as_of_today_high_priority_due}</td>
                                <td style={styles.footerTh}></td>
                                <td style={styles.footerTh}>{totals.future}</td>
                                <td style={styles.footerTh}>{totals.total}</td>
                            </tr>
                        </tfoot>
                    </table>
                )}
            </div>
        </div>
    );
};

export default TutorSupportReport;